"use strict";

(() => {
  const selectors = {
    bar: "[data-sticky-atc]",
    button: "[data-sticky-atc-button]",
    buttonText: "[data-sticky-atc-text]",
    idInput: '[name="id"]',
    minus: "[data-sticky-atc-minus]",
    price: "[data-sticky-atc-price]",
    productJson: "[data-product-json]",
    variant: "[data-sticky-atc-variant]",
    quantity: "[data-sticky-atc-quantity]",
    quantityInput: '[name="quantity"]',
    plus: "[data-sticky-atc-plus]",
    productSection: '[data-section-type="product-template"]',
    primaryButton: "[data-add-to-cart]",
    primaryButtonText: "[data-add-to-cart-text]",
  };

  const classes = {
    bodyVisible: "sticky-atc-visible",
    soldOut: "sticky-atc--sold-out",
    visible: "is-visible",
  };

  const query = (root, selector) => root?.querySelector(selector);

  function initStickyAtc(root = document) {
    root.querySelectorAll(selectors.bar).forEach((bar) => {
      if (bar.dataset.stickyAtcInit === "true") return;
      initStickyAtcBar(bar);
    });
  }

  function formatWithDelimiters(number, precision = 2, thousands = ",", decimal = ".") {
    if (Number.isNaN(number) || number == null) return "0";

    const parts = (number / 100).toFixed(precision).split(".");
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    const cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  function formatMoney(cents) {
    if (cents === 0 && window.theme?.strings?.free) return window.theme.strings.free;

    const formatter = window.themeVendor?.themeCurrency;
    const format = window.theme?.settings?.currency_code_enable ? window.theme.moneyWithCurrencyFormat : window.theme?.moneyFormat;

    if (formatter?.formatMoney && format) {
      return formatter.formatMoney(cents, format);
    }

    if (!format) return (cents / 100).toFixed(2);

    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const token = placeholder ? placeholder[1] : "amount";
    let value = "";

    switch (token) {
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
        break;
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function getProduct(section) {
    const productJson = query(section, selectors.productJson)?.textContent;

    if (!productJson) return null;

    try {
      return JSON.parse(productJson);
    } catch (error) {
      console.error("Sticky ATC product JSON could not be parsed.", error);
      return null;
    }
  }

  function getVariant(product, variantId) {
    return product?.variants?.find((variant) => String(variant.id) === String(variantId));
  }

  function getStickyButtonText(primaryButton, variant) {
    const strings = window.theme?.strings || {};

    if (!variant) return strings.unavailable || "Unavailable";

    if (variant.available === false) {
      return primaryButton.hasAttribute("data-notification-popup") ? "Notify Me" : strings.sold_out || "Sold Out";
    }

    const primaryText = query(primaryButton, selectors.primaryButtonText);

    if (!primaryText) return "";

    const textClone = primaryText.cloneNode(true);
    textClone.querySelectorAll("[data-product-price]").forEach((price) => price.remove());

    return textClone.textContent.trim();
  }

  function getQuantityBounds(quantityInput) {
    const min = Number(quantityInput?.getAttribute("min") || 1);
    const max = Number(quantityInput?.getAttribute("max"));

    return {
      min: Number.isFinite(min) && min > 0 ? min : 1,
      max: Number.isFinite(max) && max > 0 ? max : null,
    };
  }

  function clampQuantity(value, bounds) {
    const quantity = Math.max(bounds.min, value);
    return bounds.max ? Math.min(quantity, bounds.max) : quantity;
  }

  function initStickyAtcBar(bar) {
    const section = bar.closest(selectors.productSection);
    const stickyButton = query(bar, selectors.button);
    const stickyText = query(bar, selectors.buttonText);
    const stickyPrice = query(bar, selectors.price);
    const stickyVariant = query(bar, selectors.variant);
    const stickyQuantity = query(bar, selectors.quantity);
    const stickyMinus = query(bar, selectors.minus);
    const stickyPlus = query(bar, selectors.plus);
    const primaryButton = query(section, selectors.primaryButton);
    const productForm = primaryButton?.closest("form");
    const quantityInput = query(productForm, selectors.quantityInput);
    const idInput = query(productForm, selectors.idInput);
    const product = getProduct(section);

    if (!section || !stickyButton || !primaryButton) return;

    bar.dataset.stickyAtcInit = "true";

    let currentVariant = getVariant(product, idInput?.value);
    let quantity = Number(quantityInput?.value || 1);
    let visibilityFrame = null;

    const getCurrentQuantity = () => {
      const inputQuantity = Number(quantityInput?.value || quantity || 1);
      const safeQuantity = Number.isFinite(inputQuantity) ? inputQuantity : 1;

      return clampQuantity(safeQuantity, getQuantityBounds(quantityInput));
    };

    const setVisible = (isVisible) => {
      bar.classList.toggle(classes.visible, isVisible);
      document.body.classList.toggle(classes.bodyVisible, isVisible);
      bar.setAttribute("aria-hidden", isVisible ? "false" : "true");
    };

    const sync = () => {
      const isSoldOut = Boolean(currentVariant && currentVariant.available === false);

      quantity = getCurrentQuantity();
      bar.classList.toggle(classes.soldOut, isSoldOut);
      stickyButton.disabled = primaryButton.disabled || primaryButton.hasAttribute("disabled");
      if (stickyMinus) stickyMinus.disabled = quantity <= 1;
      if (stickyText) stickyText.textContent = getStickyButtonText(primaryButton, currentVariant);
      if (stickyQuantity) stickyQuantity.textContent = quantity;
      if (currentVariant && stickyPrice) stickyPrice.textContent = formatMoney(currentVariant.price * quantity);
    };

    const syncVisibility = () => {
      visibilityFrame = null;
      setVisible(section.getBoundingClientRect().bottom <= 0);
      sync();
    };

    const scheduleVisibilitySync = () => {
      if (visibilityFrame) return;
      visibilityFrame = window.requestAnimationFrame(syncVisibility);
    };

    const updateQuantity = (nextQuantity) => {
      quantity = clampQuantity(nextQuantity, getQuantityBounds(quantityInput));

      if (quantityInput) {
        quantityInput.value = quantity;
        quantityInput.dispatchEvent(new Event("change", {bubbles: true}));
        return;
      }

      sync();
    };

    stickyButton.addEventListener("click", () => {
      if (stickyButton.disabled) return;
      updateQuantity(quantity);
      primaryButton.click();
    });

    stickyMinus?.addEventListener("click", () => updateQuantity(quantity - 1));
    stickyPlus?.addEventListener("click", () => updateQuantity(quantity + 1));
    quantityInput?.addEventListener("change", sync);

    section.addEventListener("theme:variant:change", (event) => {
      const variant = event.detail?.variant;
      currentVariant = getVariant(product, variant?.id) || variant;

      if (stickyVariant) {
        const variantTitle = variant?.title || "";
        stickyVariant.textContent = variantTitle === "Default Title" ? "" : variantTitle;
        stickyVariant.parentElement?.classList.toggle("is-hidden", !stickyVariant.textContent);
      }

      sync();
    });

    new MutationObserver(sync).observe(primaryButton, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    window.addEventListener("scroll", scheduleVisibilitySync, {passive: true});
    window.addEventListener("resize", scheduleVisibilitySync);
    document.addEventListener("theme:resize", scheduleVisibilitySync);

    sync();
    scheduleVisibilitySync();
  }

  document.addEventListener("DOMContentLoaded", () => initStickyAtc());
  document.addEventListener("shopify:section:load", (event) => initStickyAtc(event.target));
})();
